import {
  MODEL_PRICING,
  PREPAID_TIER_MULTIPLIERS,
  VALID_PROVIDER_TIERS,
} from '../../../../../shared-pricing-config'
import {
  GENERATION_MODEL_CATALOG,
  getAllBaseModels,
} from '../../../../../shared-generation-pricing-config'
import type {
  ModelFamily,
  ModelPricing,
  ProviderTier,
} from '../../../../../shared-pricing-config'
import type { GenerationCategory } from '../../../../../shared-generation-pricing-config'

/* ------------------------------------------------------------------ */
/* Catalog facts — derived from the pricing configs so the home page   */
/* can never drift from what billing and /models actually serve.       */
/* ------------------------------------------------------------------ */

const chatModelIds = Object.keys(MODEL_PRICING)

const familyCounts = chatModelIds.reduce<Partial<Record<ModelFamily, number>>>(
  (acc, id) => {
    const family = MODEL_PRICING[id].family
    acc[family] = (acc[family] ?? 0) + 1
    return acc
  },
  {},
)

export const chatModelCount = chatModelIds.length
export const mediaModelCount = getAllBaseModels().length
export const totalModelCount = chatModelCount + mediaModelCount
export const providerCount = Object.keys(familyCounts).length
export const maxDiscountPercent = Math.round(
  (1 - PREPAID_TIER_MULTIPLIERS.economy) * 100,
)

/** Four facts under the hero. Every value is computed, none is typed in. */
export const proofFacts = [
  { value: chatModelCount, suffix: '', label: 'chat & reasoning models' },
  { value: mediaModelCount, suffix: '', label: 'image, video & audio models' },
  { value: providerCount, suffix: '', label: 'providers behind one key' },
  { value: maxDiscountPercent, suffix: '%', label: 'off at the economy tier' },
] as const

/* ------------------------------------------------------------------ */
/* Hero catalog panel                                                   */
/* ------------------------------------------------------------------ */

export interface RosterModel {
  id: string
  name: string
  family: ModelFamily
  inputPerMillion: number
  outputPerMillion: number
  context: string
}

const HERO_ROSTER_IDS = [
  'claude-sonnet-5',
  'gpt-5.6',
  'gemini-3.1-pro',
  'deepseek-v3.2',
  'kimi-k3',
  'grok-4.6',
]

function toRosterModel(id: string): Array<RosterModel> {
  // A renamed id in config should drop a row, never 500 the landing page.
  const pricing = MODEL_PRICING[id] as ModelPricing | undefined
  if (!pricing) return []
  return [
    {
      id,
      name: pricing.displayName,
      family: pricing.family,
      inputPerMillion: pricing.inputPricePerMillion,
      outputPerMillion: pricing.outputPricePerMillion,
      context: pricing.maxContext,
    },
  ]
}

export const heroRoster: Array<RosterModel> =
  HERO_ROSTER_IDS.flatMap(toRosterModel)

/* ------------------------------------------------------------------ */
/* Provider tiers                                                       */
/* ------------------------------------------------------------------ */

const tierCopy: Record<
  ProviderTier,
  { name: string; summary: string; wallet: string }
> = {
  economy: {
    name: 'Economy',
    summary: 'Lowest cost. Tried first on every request.',
    wallet: 'Prepaid or bonus balance',
  },
  standard: {
    name: 'Standard',
    summary: 'Balanced value. Next in line when economy is unavailable.',
    wallet: 'Prepaid or bonus balance',
  },
  premium: {
    name: 'Premium',
    summary: 'Official provider APIs with the highest uptime. Final fallback.',
    wallet: 'Prepaid balance only',
  },
}

export const providerTiers = VALID_PROVIDER_TIERS.map((tier) => {
  const multiplier = PREPAID_TIER_MULTIPLIERS[tier]
  return {
    tier,
    ...tierCopy[tier],
    multiplier,
    discountPercent: Math.round((1 - multiplier) * 100),
  }
})

/** Worked example used on every tier card: what $10 of provider usage costs you. */
export const TIER_EXAMPLE_SPEND_DOLLARS = 10

/* ------------------------------------------------------------------ */
/* Agent teams                                                          */
/* ------------------------------------------------------------------ */

const COORDINATOR_MODEL_ID = 'claude-sonnet-4-6'
const SPECIALIST_MODEL_ID = 'claude-opus-4-6'

/** The example team every agent-team visual on the public site is built from. */
export const AGENT_TEAM_ID = 'my-coding-agent'

/** A specialist's response mode: who writes the reply the user receives. */
export type ResponseMode = 'advise' | 'delegate'

export interface FlowStep {
  kind: 'request' | 'coordinator' | 'specialist' | 'reply'
  label: string
  /** How the arrow leading into this step is labelled. */
  via?: { label: string; mode: ResponseMode }
}

export interface FlowLaneData {
  title: string
  /** Illustrative share of requests that take this path on a coding workload. */
  share: number
  modelId: string
  steps: Array<FlowStep>
}

/**
 * Three paths a request can take through the example team. Shares are
 * illustrative; the model ids and response-mode semantics are real.
 */
export const flowLanes: Array<FlowLaneData> = [
  {
    title: 'Coordinator answers',
    share: 68,
    modelId: COORDINATOR_MODEL_ID,
    steps: [
      { kind: 'request', label: 'Request' },
      { kind: 'coordinator', label: 'Coordinator' },
      { kind: 'reply', label: 'Reply' },
    ],
  },
  {
    title: 'Senior Architect advises',
    share: 20,
    modelId: SPECIALIST_MODEL_ID,
    steps: [
      { kind: 'request', label: 'Request' },
      { kind: 'coordinator', label: 'Coordinator' },
      { kind: 'specialist', label: 'Senior Architect' },
      {
        kind: 'coordinator',
        label: 'Coordinator',
        via: { label: 'advises', mode: 'advise' },
      },
      { kind: 'reply', label: 'Reply' },
    ],
  },
  {
    title: 'Reviewer delegates',
    share: 12,
    modelId: SPECIALIST_MODEL_ID,
    steps: [
      { kind: 'request', label: 'Request' },
      { kind: 'coordinator', label: 'Coordinator' },
      { kind: 'specialist', label: 'Reviewer' },
      {
        kind: 'reply',
        label: 'Reply',
        via: { label: 'delegates', mode: 'delegate' },
      },
    ],
  },
]

function inputPrice(id: string): number {
  return (
    (MODEL_PRICING[id] as ModelPricing | undefined)?.inputPricePerMillion ?? 0
  )
}

/** Blended input $/M for the example split vs. sending everything to the specialist. */
export const agentTeamCost = {
  blendedPerMillion: flowLanes.reduce(
    (sum, lane) => sum + (lane.share / 100) * inputPrice(lane.modelId),
    0,
  ),
  allSpecialistPerMillion: inputPrice(SPECIALIST_MODEL_ID),
  coordinatorPerMillion: inputPrice(COORDINATOR_MODEL_ID),
  specialistPerMillion: inputPrice(SPECIALIST_MODEL_ID),
}

export const howItWorks = [
  {
    title: 'Build it on a canvas',
    body: 'Pick a coordinator model, add specialists from the roster, and set each one’s model, fallback and rules in place.',
  },
  {
    title: 'Choose who writes the reply',
    body: 'Advise sends a specialist’s answer back to the coordinator to combine. Delegate sends it to the user exactly as written.',
  },
  {
    title: 'Call it like a model',
    body: 'Use the team id as the model. Same request format, same SDK, same streaming. Each agent bills at its own model’s rate.',
  },
] as const

/* ------------------------------------------------------------------ */
/* Media generation                                                     */
/* ------------------------------------------------------------------ */

const mediaCopy: Record<
  GenerationCategory,
  { title: string; body: string }
> = {
  image: {
    title: 'Image',
    body: 'Text-to-image, editing and upscaling.',
  },
  video: {
    title: 'Video',
    body: 'Text-to-video and image-to-video.',
  },
  audio: {
    title: 'Audio',
    body: 'Text-to-speech, plus audio-driven video and lip-sync.',
  },
}

/** Pure speech models first; the audio category also holds audio-to-video. */
function isTextToAudio(id: string): boolean {
  return 'TEXT_TO_AUDIO' in GENERATION_MODEL_CATALOG[id].variants
}

export const mediaCategories = (
  ['image', 'video', 'audio'] satisfies Array<GenerationCategory>
).map((category) => {
  const ids = getAllBaseModels(category).sort(
    (a, b) => Number(isTextToAudio(b)) - Number(isTextToAudio(a)),
  )
  const names = [
    ...new Set(ids.map((id) => GENERATION_MODEL_CATALOG[id].displayName)),
  ]
  return { category, count: ids.length, names, ...mediaCopy[category] }
})

/* ------------------------------------------------------------------ */
/* Model catalog                                                        */
/* ------------------------------------------------------------------ */

const familyLabels: Partial<
  Record<ModelFamily, { name: string; vendor: string }>
> = {
  anthropic: { name: 'Claude', vendor: 'Anthropic' },
  openai: { name: 'GPT / o-series', vendor: 'OpenAI' },
  google: { name: 'Gemini', vendor: 'Google' },
  deepseek: { name: 'DeepSeek', vendor: 'DeepSeek' },
  moonshot: { name: 'Kimi', vendor: 'Moonshot' },
  xai: { name: 'Grok', vendor: 'xAI' },
  qwen: { name: 'Qwen', vendor: 'Alibaba' },
  minimax: { name: 'MiniMax', vendor: 'MiniMax' },
  zai: { name: 'GLM', vendor: 'Zhipu' },
  stepfun: { name: 'Step', vendor: 'StepFun' },
  xiaomi: { name: 'MiMo', vendor: 'Xiaomi' },
}

export const modelProviders = (
  Object.entries(familyCounts) as Array<[ModelFamily, number]>
)
  .map(([family, count]) => ({
    family,
    count,
    name: familyLabels[family]?.name ?? family,
    vendor: familyLabels[family]?.vendor ?? family,
  }))
  .sort((a, b) => b.count - a.count)

/* ------------------------------------------------------------------ */
/* Ecosystem                                                            */
/* ------------------------------------------------------------------ */

/** What an agent gains once the MCP server is connected. */
export const mcpCapabilities = [
  {
    title: 'Generate media mid-task',
    body: 'Your agent creates images, video and audio itself — no context switch, no separate dashboard.',
  },
  {
    title: 'Delegate to cheaper models',
    body: 'Hand bulk work to a smaller model through one tool call and keep the expensive one for the hard parts.',
  },
  {
    title: 'Discover models at runtime',
    body: 'Live catalogs of every model, the parameters each accepts and what it costs.',
  },
] as const

/** Tools the MCP server advertises. */
export const mcpTools = [
  'generate_image',
  'generate_video',
  'generate_audio',
  'get_generation',
  'upload_media',
  'llm_chat',
  'list_models',
  'list_generation_models',
] as const

/** Coding tools with a setup guide under /docs/integrations. */
export const compatibleTools = [
  { name: 'Cursor', domain: 'cursor.com', doc: 'integrations/cursor' },
  { name: 'Claude Code', domain: 'claude.ai', doc: 'integrations/claude-code' },
  { name: 'Codex', domain: 'openai.com', doc: 'integrations/codex' },
  {
    name: 'Gemini CLI',
    domain: 'gemini.google.com',
    doc: 'integrations/gemini-cli',
  },
  { name: 'Windsurf', domain: 'windsurf.com', doc: 'integrations/windsurf' },
  { name: 'OpenCode', domain: 'opencode.ai', doc: 'integrations/opencode' },
  { name: 'Continue', domain: 'continue.dev', doc: 'integrations/continue' },
  { name: 'Cline', domain: 'cline.bot', doc: 'integrations/cline' },
  { name: 'Roo Code', domain: 'roocode.com', doc: 'integrations/roo-code' },
  { name: 'Aider', domain: 'aider.chat', doc: 'integrations/aider' },
  { name: 'Zed', domain: 'zed.dev', doc: 'integrations/zed' },
  { name: 'Amp Code', domain: 'ampcode.com', doc: 'integrations/amp-code' },
  {
    name: 'Droids',
    domain: 'factory.ai',
    doc: 'integrations/droids',
    favicon: 'https://factory.ai/favicon.svg',
  },
] as const
